import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import MainLayout from './components/Layout/MainLayout';
import ProjectLayout from './components/Layout/ProjectLayout';
import ProjectList from './pages/Projects/ProjectList';
import ProjectDetail from './pages/Projects/ProjectDetail';
import ContactList from './pages/Contacts/ContactList';
import AnonymousList from './pages/Anonymous/AnonymousList';
import GroupList from './pages/Groups/GroupList';
import AutomationList from './pages/Automations/AutomationList';
import AutomationForm from './pages/Automations/AutomationForm';
import EmailTemplateList from './pages/EmailTemplates/EmailTemplateList';
import EmailTemplateForm from './pages/EmailTemplates/EmailTemplateForm';
import ContactPropertyList from './pages/ContactProperties/ContactPropertyList';
import ImportContacts from './pages/Contacts/ImportContacts';
import ContactDashboard from './pages/Dashboard/ContactDashboard';
import UserList from './pages/Users/UserList';
import Profile from './pages/Users/Profile';
import Login from './pages/Auth/Login';
import Signup from './pages/Auth/Signup';
import { useAuthStore } from './store/authStore';
import { CircularProgress, Box } from '@mui/material';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuthStore();
  const location = useLocation();

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children || <Outlet />;
};

const GuestRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuthStore();

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children || <Outlet />;
};

const App = () => {
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const loading = useAuthStore((state) => state.loading);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Routes>
      <Route path="/" element={<MainLayout />}>
        {/* Auth Routes - Guest only */}
        <Route path="login" element={<GuestRoute><Login /></GuestRoute>} />
        <Route path="signup" element={<GuestRoute><Signup /></GuestRoute>} />

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          {/* Project Routes */}
          <Route index element={<ProjectList />} />
          <Route path="project" element={<ProjectList />} />

          {/* Project Layout Wrapper */}
          <Route element={<ProjectLayout />}>
            <Route path="project/new" element={<ProjectDetail />} />
            <Route path="project/:id" element={<ProjectDetail />} />

            {/* Contact & Visitor Routes */}
            <Route path="project/:project/contact" element={<ContactList />} />
            <Route path="project/:project/anonymous" element={<AnonymousList />} />

            {/* Group Routes */}
            <Route path="project/:project/group" element={<GroupList />} />
            <Route path="project/:project/group/new" element={<GroupList />} />

            {/* Automation Routes */}
            <Route path="project/:project/automation" element={<AutomationList />} />
            <Route path="project/:project/automation/new" element={<AutomationForm />} />
            <Route path="project/:project/automation/:id" element={<AutomationForm />} />

            {/* Email Template Routes */}
            <Route path="project/:project/emailtemplate" element={<EmailTemplateList />} />
            <Route path="project/:project/emailtemplate/new" element={<EmailTemplateForm />} />
            <Route path="project/:project/emailtemplate/:id" element={<EmailTemplateForm />} />

            {/* Contact Property Routes */}
            <Route path="project/:project/contactproperty" element={<ContactPropertyList />} />

            {/* Contact Import Routes */}
            <Route path="project/:project/importcontact" element={<ImportContacts />} />

            {/* Dashboard Routes */}
            <Route path="project/:project/contactdashboard" element={<ContactDashboard />} />
          </Route>

          {/* User Routes */}
          <Route path="user" element={<UserList />} />
          <Route path="profile" element={<Profile />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default App;
